import React, { useEffect, useState } from "react";
import axios from "axios";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";
import { toast } from "../../helpers/swal";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { AiOutlineHome } from "react-icons/ai";
import { useNavigate } from "react-router-dom";

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [allShops, setAllShops] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const [assignedShops, setAssignedShops] = useState([]);
  const [unassignedShops, setUnassignedShops] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [shipOwner, setShipOwner] = useState("maya");

  useEffect(() => {
    if (!editingUser?.id) return;

    setShipOwner(editingUser.shipstation_owner || "maya");
  }, [editingUser]);

  const fetchStoresFromShipStation = () => {
    axios
      .get(`${settings.apiURL}/fetch_shipstation_stores.php`, {
        headers: authHeader(),
      })
      .then((res) => {
        if (res.data.success) {
          toast(`${res.data.count} store(s) updated`, "success");
          fetchAllShops();
        } else {
          toast(res.data.message || "Store fetch failed", "error");
        }
      })
      .catch((err) => {
        toast("Error fetching stores", "error");
        console.error(err);
      });
  };

  const fetchUsers = () => {
    axios
      .get(`${settings.apiURL}/admin_get_users.php`, { headers: authHeader() })
      .then((res) => {
        if (res.data.success) {
          setUsers(res.data.users);
        } else {
          toast("Failed to load users", "error");
        }
      })
      .catch(() => toast("Failed to fetch users", "error"));
  };

  const fetchAllShops = () => {
    axios
      .get(`${settings.apiURL}/admin_get_all_shops.php`, {
        headers: authHeader(),
      })
      .then((res) => {
        if (res.data.success) {
          setAllShops(res.data.shops);
        }
      })
      .catch(() => toast("Shop fetch failed", "error"));
  };

  useEffect(() => {
    fetchUsers();
    fetchAllShops();
  }, []);

  const handleEdit = (user) => {
    const userShopIds = (user.shops || []).map((s) => s.id);

    setAssignedShops(allShops.filter((shop) => userShopIds.includes(shop.id)));
    setUnassignedShops(
      allShops.filter((shop) => !userShopIds.includes(shop.id))
    );

    setEditingUser({ ...user, shop_ids: userShopIds });
    setShipOwner(user.shipstation_owner || "maya");

    setShowModal(true);
  };

  const handleSave = () => {
    const updatedUser = {
      user_id: editingUser.id,
      shop_ids: assignedShops.map((shop) => shop.id),
      status: editingUser.status,
      shipstation_owner: shipOwner,
    };

    axios
      .post(`${settings.apiURL}/admin_update_user_shops.php`, updatedUser, {
        headers: authHeader(),
      })
      .then((res) => {
        if (res.data.success) {
          toast("User status and shops updated", "success");
          setShowModal(false);
          fetchUsers();
        } else {
          toast("Update failed", "error");
        }
      })
      .catch(() => toast("Update error", "error"));
  };

  const handleDelete = () => {
    if (!editingUser?.id) return;

    if (window.confirm("Are you sure you want to delete this user?")) {
      axios
        .post(
          `${settings.apiURL}/admin_delete_user.php`,
          { id: editingUser.id },
          {
            headers: authHeader(),
          }
        )
        .then((res) => {
          if (res.data.success) {
            toast("User deleted", "success");
            setShowModal(false);
            setEditingUser(null);
            fetchUsers();
          } else {
            toast("Delete failed", "error");
          }
        })
        .catch(() => toast("Delete error", "error"));
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const displayedUsers = users
    .filter((user) => {
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
      const email = user.email?.toLowerCase() || "";
      const shops =
        user.shops
          ?.map((s) => s.shop_name)
          .join(", ")
          .toLowerCase() || "";

      const term = searchTerm.toLowerCase();
      return (
        fullName.includes(term) || email.includes(term) || shops.includes(term)
      );
    })
    .sort((a, b) => {
      if (!sortField) return 0;

      let valA = a[sortField];
      let valB = b[sortField];

      if (sortField === "name") {
        valA = `${a.first_name} ${a.last_name}`.toLowerCase();
        valB = `${b.first_name} ${b.last_name}`.toLowerCase();
      } else if (sortField === "status") {
        valA = a.status;
        valB = b.status;
      } else {
        valA = valA?.toLowerCase?.() || "";
        valB = valB?.toLowerCase?.() || "";
      }

      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

  return (
    <div className="container mt-4">
      <div className="mb-4 text-center">
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate("/home")}
        >
          <AiOutlineHome style={{ marginRight: "6px" }} />
          Home
        </button>
      </div>

      <h3 className="text-center">Manage Users</h3>
      <div className="text-center mb-3">
        <Button
          variant="outline-dark"
          size="sm"
          onClick={fetchStoresFromShipStation}
        >
          Fetch ShipStation Stores
        </Button>
      </div>
      <div className="mb-3 text-center">
        <input
          type="text"
          className="form-control"
          placeholder="Search by name, email, or shop..."
          style={{ maxWidth: "400px", margin: "0 auto" }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <table className="table table-bordered mt-3">
        <thead>
          <tr>
            <th>#</th>
            <th
              style={{ cursor: "pointer" }}
              onClick={() => handleSort("name")}
            >
              Ad Soyad{" "}
              {sortField === "name" && (sortOrder === "asc" ? "▲" : "▼")}
            </th>
            <th
              style={{ cursor: "pointer" }}
              onClick={() => handleSort("email")}
            >
              Email {sortField === "email" && (sortOrder === "asc" ? "▲" : "▼")}
            </th>
            <th>Shops</th>
            <th
              style={{ cursor: "pointer" }}
              onClick={() => handleSort("status")}
            >
              Status{" "}
              {sortField === "status" && (sortOrder === "asc" ? "▲" : "▼")}
            </th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {displayedUsers.map((user, i) => (
            <tr key={user.id}>
              <td>{i + 1}</td>
              <td>
                {user.first_name} {user.last_name}
              </td>
              <td>{user.email}</td>
              <td>
                {Array.isArray(user.shops) && user.shops.length > 0
                  ? user.shops.map((s) => s.shop_name).join(", ")
                  : "-"}
              </td>
              <td>{user.status === 1 ? "Active" : "Inactive"}</td>
              <td>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => handleEdit(user)}
                >
                  Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ✨ Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {editingUser && (
            <>
              <div className="mb-2">
                <label>First Name</label>
                <input
                  className="form-control"
                  name="first_name"
                  value={editingUser.first_name}
                  onChange={(e) =>
                    setEditingUser({
                      ...editingUser,
                      first_name: e.target.value,
                    })
                  }
                />
              </div>

              <div className="mb-2">
                <label>Last Name</label>
                <input
                  className="form-control"
                  name="last_name"
                  value={editingUser.last_name}
                  onChange={(e) =>
                    setEditingUser({
                      ...editingUser,
                      last_name: e.target.value,
                    })
                  }
                />
              </div>

              <div className="mb-2">
                <label>Email</label>
                <input
                  className="form-control"
                  name="email"
                  value={editingUser.email}
                  onChange={(e) =>
                    setEditingUser({ ...editingUser, email: e.target.value })
                  }
                />
              </div>

              <div className="mb-2">
                <label>Status</label>
                <select
                  className="form-select"
                  name="status"
                  value={editingUser.status}
                  onChange={(e) =>
                    setEditingUser({ ...editingUser, status: e.target.value })
                  }
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

              <div className="mb-3">
                <label>ShipStation Owner</label>
                <div className="d-flex">
                  <div
                    onClick={() => setShipOwner("maya")}
                    className={`px-3 py-2 border rounded-start text-center flex-fill ${
                      shipOwner === "maya"
                        ? "bg-success text-white"
                        : "bg-light"
                    }`}
                    style={{ cursor: "pointer" }}
                  >
                    Maya
                  </div>
                  <div
                    onClick={() => setShipOwner("customzon")}
                    className={`px-3 py-2 border rounded-end text-center flex-fill ${
                      shipOwner === "customzon"
                        ? "bg-success text-white"
                        : "bg-light"
                    }`}
                    style={{ cursor: "pointer" }}
                  >
                    Customzon
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label>Shop Assignment</label>
                <div className="row">
                  <div className="col">
                    <strong>Available Shops</strong>
                    <ul
                      className="list-group"
                      style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        minHeight: "200px",
                      }}
                    >
                      {unassignedShops.map((shop) => (
                        <li
                          key={shop.id}
                          className="list-group-item list-group-item-action"
                          onClick={() => {
                            setAssignedShops((prev) => [...prev, shop]);
                            setUnassignedShops((prev) =>
                              prev.filter((s) => s.id !== shop.id)
                            );
                          }}
                        >
                          {shop.shop_name}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="col">
                    <strong>Assigned Shops</strong>
                    <ul className="list-group" style={{ minHeight: "200px" }}>
                      {assignedShops.map((shop) => (
                        <li
                          key={shop.id}
                          className="list-group-item list-group-item-success"
                          onClick={() => {
                            setUnassignedShops((prev) => [...prev, shop]);
                            setAssignedShops((prev) =>
                              prev.filter((s) => s.id !== shop.id)
                            );
                          }}
                        >
                          {shop.shop_name}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="danger" onClick={handleDelete}>
            Delete User
          </Button>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleSave}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminUsersPage;
